import { useState, useEffect } from 'react'
import { useNavigate, useParams, Link, useLocation, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Unit, UnitFormData, Level } from '@/types'
import {
  ArrowLeft,
  Save,
  Layers,
  Plus,
  Trash2,
  BookOpen,
  GripVertical,
  Edit,
  FolderTree
} from 'lucide-react'

export default function UnitForm() {
  const { unitId } = useParams()
  const [searchParams] = useSearchParams()
  const levelIdFromUrl = searchParams.get('level')
  const navigate = useNavigate()
  const location = useLocation()
  const isEditing = Boolean(unitId) && unitId !== 'new'
  const isViewMode = isEditing && !location.pathname.endsWith('/edit')

  const [levels, setLevels] = useState<Level[]>([])
  const [formData, setFormData] = useState<UnitFormData>({
    title: '',
    description: '',
    objectives: [''],
    order_num: 1,
    level_id: levelIdFromUrl || '',
    is_active: true
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    loadLevels()
    if (isEditing) {
      loadUnit()
    } else {
      loadNextOrderNum()
    }
  }, [unitId])

  const loadLevels = async () => {
    try {
      const { data, error } = await supabase
        .from('levels')
        .select('*')
        .eq('is_active', true)
        .order('order_num')

      if (error) throw error
      setLevels((data as unknown as Level[]) || [])
    } catch (error) {
      console.error('Error loading levels:', error)
    }
  }

  const loadUnit = async () => {
    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from('units')
        .select('*')
        .eq('id', unitId as string)
        .single()

      if (error) throw error

      const unitData = data as unknown as Unit

      setFormData({
        title: unitData.title,
        description: unitData.description || '',
        objectives: unitData.objectives || [''],
        order_num: unitData.order_num,
        level_id: unitData.level_id || '',
        is_active: unitData.is_active
      })
    } catch (error) {
      console.error('Error loading unit:', error)
      setError('Error al cargar la unidad')
    } finally {
      setIsLoading(false)
    }
  }

  const loadNextOrderNum = async () => {
    try {
      const { data } = await supabase
        .from('units')
        .select('order_num')
        .order('order_num', { ascending: false })
        .limit(1)

      const orderData = data as unknown as { order_num: number }[]
      if (orderData && orderData.length > 0) {
        setFormData(prev => ({ ...prev, order_num: orderData[0].order_num + 1 }))
      }
    } catch (error) {
      console.error('Error loading order num:', error)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSaving(true)

    try {
      // Filtrar objetivos vacios
      const objectives = formData.objectives.filter(o => o.trim() !== '')

      if (isEditing) {
        const { error } = await (supabase
          .from('units') as any)
          .update({
            title: formData.title,
            description: formData.description,
            objectives,
            order_num: formData.order_num,
            level_id: formData.level_id || null,
            is_active: formData.is_active,
            updated_at: new Date().toISOString()
          })
          .eq('id', unitId as string)

        if (error) throw error
      } else {
        const { error } = await (supabase
          .from('units') as any)
          .insert({
            title: formData.title,
            description: formData.description,
            objectives,
            order_num: formData.order_num,
            level_id: formData.level_id || null,
            is_active: formData.is_active
          })

        if (error) throw error
      }

      navigate('/admin/units')
    } catch (error: any) {
      console.error('Error saving unit:', error)
      setError(error.message || 'Error al guardar la unidad')
    } finally {
      setIsSaving(false)
    }
  }

  const addObjective = () => {
    setFormData(prev => ({
      ...prev,
      objectives: [...prev.objectives, '']
    }))
  }

  const removeObjective = (index: number) => {
    setFormData(prev => ({
      ...prev,
      objectives: prev.objectives.filter((_, i) => i !== index)
    }))
  }

  const updateObjective = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      objectives: prev.objectives.map((o, i) => i === index ? value : o)
    }))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/units"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isViewMode ? 'Visualizar Unidad' : (isEditing ? 'Editar Unidad' : 'Nueva Unidad')}
            </h1>
            <p className="text-gray-600">
              {isViewMode ? 'Vista previa de la unidad' : (isEditing ? 'Modifica los datos de la unidad' : 'Crea una nueva unidad para el curso')}
            </p>
          </div>
        </div>
        {isViewMode && (
          <Link
            to={`/admin/units/${unitId}/edit`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Edit className="w-5 h-5" />
            Editar
          </Link>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}

        {/* Basic Info */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-green-100">
              <Layers className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Información Básica</h2>
          </div>

          {/* Nivel */}
          <div>
            <label htmlFor="unit-level" className="block text-sm font-medium text-gray-700 mb-1">
              <FolderTree className="w-4 h-4 inline mr-1" />
              Nivel
            </label>
            <select
              id="unit-level"
              value={formData.level_id}
              onChange={(e) => setFormData(prev => ({ ...prev, level_id: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isViewMode}
            >
              <option value="">Sin nivel asignado</option>
              {levels.map(level => (
                <option key={level.id} value={level.id}>
                  Nivel {level.order_num}: {level.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Asigna esta unidad a un nivel para organizarla en cohortes
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="unit-order" className="block text-sm font-medium text-gray-700 mb-1">
                Orden
              </label>
              <input id="unit-order" type="number" 
                min="1"
                value={formData.order_num}
                onChange={(e) => setFormData(prev => ({ ...prev, order_num: Number.parseInt(e.target.value) || 1 }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={isViewMode}
              />
            </div>
            <div className="md:col-span-3">
              <label htmlFor="unit-title" className="block text-sm font-medium text-gray-700 mb-1">
                Título de la Unidad
              </label>
              <input id="unit-title" type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Ej: Unidad 1 - Saludos y Presentaciones"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={isViewMode}
              />
            </div>
          </div>

          <div>
            <label htmlFor="unit-description" className="block text-sm font-medium text-gray-700 mb-1">
              Descripción
            </label>
            <textarea
              id="unit-description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe el contenido y objetivos generales de esta unidad..."
              rows={3}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isViewMode}
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
              className="w-4 h-4 text-[#0055A4] border-gray-300 rounded focus:ring-[#0055A4] disabled:cursor-not-allowed"
              disabled={isViewMode}
            />
            <label htmlFor="is_active" className="text-sm text-gray-700">
              Unidad activa (visible para estudiantes)
            </label>
          </div>
        </div>

        {/* Objectives */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#0055A4]/10">
                <BookOpen className="w-5 h-5 text-[#0055A4]" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Objetivos de Aprendizaje</h2>
            </div>
            {!isViewMode && (
              <button
                type="button"
                onClick={addObjective}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-[#0055A4] hover:bg-[#0055A4]/5 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                Agregar
              </button>
            )}
          </div>

          <div className="space-y-3">
            {formData.objectives.map((objective, index) => (
              <div key={index} className="flex items-center gap-2">
                {!isViewMode && <GripVertical className="w-5 h-5 text-gray-300 cursor-move" />}
                <input
                  type="text"
                  value={objective}
                  onChange={(e) => updateObjective(index, e.target.value)}
                  placeholder={`Objetivo ${index + 1}`}
                  className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                  disabled={isViewMode}
                />
                {formData.objectives.length > 1 && !isViewMode && (
                  <button
                    type="button"
                    onClick={() => removeObjective(index)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        {!isViewMode && (
          <div className="flex justify-end gap-3">
            <Link
              to="/admin/units"
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </Link>
            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] transition-colors disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {isSaving ? 'Guardando...' : (isEditing ? 'Guardar Cambios' : 'Crear Unidad')}
            </button>
          </div>
        )}
      </form>
    </div>
  )
}
